import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "react-toastify";
import CloseIcon from "@mui/icons-material/Close";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import PrintIcon from "@mui/icons-material/Print";
import SmsOutlinedIcon from "@mui/icons-material/SmsOutlined";
import baseUrl from "../../../../api/baseUrl";
import AppointmentPatientStep, {
  type PatientStepMode,
} from "../../makeappointment/calender/AppointmentPatientStep";
import {
  buildNewAppointmentFormState,
  type NewAppointmentFormState,
} from "../../makeappointment/calender/newAppointmentForm";
import { formatPatientNameWithTitle } from "./appointmentViewShared";
import { notifySmsBalanceChanged } from "../../../../utils/sms/smsToast";

/**
 * Change (or assign) the patient on an appointment that has not progressed.
 * The server decides eligibility: status still upcoming, not cancelled, no
 * status changes, no treatment sheet or payment linked. Its reason is shown
 * as-is when it refuses.
 *
 * The patient picker is the same step used in Make Appointment: type a name
 * or phone, pick a match, or "Register as new" when nobody matches. A new
 * patient is created and linked in one server call.
 *
 * After a successful swap the modal offers a one-click confirmation SMS to
 * the new patient and, when a token was issued at booking, reminds reception
 * to reprint the token slip.
 */

type SwapResult = {
  hadToken: boolean;
  newPatientCreated: boolean;
  regNo?: string;
  patientName: string;
  mobile: string;
};

interface Props {
  appointment: Record<string, any>;
  open: boolean;
  onClose: () => void;
  /** Called after a successful swap so the caller can refresh the list. */
  onSwapped: () => void;
}

export default function SwapPatientModal({ appointment, open, onClose, onSwapped }: Props) {
  const currentPatientId: string | null = (() => {
    const raw = appointment?.patientId;
    if (!raw) return null;
    if (typeof raw === "string") return raw;
    return raw?._id ? String(raw._id) : null;
  })();
  const currentName =
    formatPatientNameWithTitle(appointment?.patientId) ||
    formatPatientNameWithTitle(appointment?.bookPatientId) ||
    appointment?.bookPatientId?.fullName ||
    "";
  const hasPatient = Boolean(currentPatientId);

  const [formData, setFormData] = useState<NewAppointmentFormState>(() =>
    buildNewAppointmentFormState({})
  );
  const [patientMode, setPatientMode] = useState<PatientStepMode>("entry");
  const [error, setError] = useState(false);
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState<SwapResult | null>(null);
  const [smsSending, setSmsSending] = useState(false);

  useEffect(() => {
    if (!open) return;
    setFormData(buildNewAppointmentFormState({}));
    setPatientMode("entry");
    setError(false);
    setSaving(false);
    setResult(null);
    setSmsSending(false);
  }, [open, appointment?._id]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        if (!saving) onClose();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, saving, onClose]);

  const handleConfirm = () => {
    if (saving || !appointment?._id) return;

    const payload: Record<string, unknown> = {
      appointmentId: appointment._id,
    };
    if (patientMode === "existing" && formData.patientId) {
      if (String(formData.patientId) === String(currentPatientId ?? "")) {
        toast.error("This appointment is already booked for that patient.");
        return;
      }
      payload.mode = "existing";
      payload.patientId = formData.patientId;
    } else if (patientMode === "new") {
      const fullName = String(formData.fullName || "").trim();
      const digits = String(formData.mobileNo || "").replace(/\D/g, "");
      if (!fullName || !formData.gender || digits.length < 5) {
        setError(true);
        return;
      }
      payload.mode = "new";
      payload.newPatient = {
        title: formData.title,
        fullName,
        gender: formData.gender,
        mobileNo: formData.mobileNo,
        ...(formData.dob ? { dob: formData.dob } : {}),
        ...(String(formData.address || "").trim()
          ? { address: String(formData.address).trim() }
          : {}),
      };
    } else {
      setError(true);
      return;
    }

    setSaving(true);
    baseUrl
      .patch("/appointment/swapPatient", payload)
      .then((res) => {
        if (!res.data?.success) {
          toast.error(res.data?.error || res.data?.message || "Could not change patient");
          return;
        }
        const appt = res.data?.data;
        const swap = res.data?.swap || {};
        setResult({
          hadToken: Boolean(swap.hadToken),
          newPatientCreated: Boolean(swap.newPatientCreated),
          regNo: swap.regNo || appt?.patientId?.regNo,
          patientName:
            formatPatientNameWithTitle(appt?.patientId) || appt?.patientId?.fullName || "",
          mobile: appt?.patientId?.mobileNo || "",
        });
        toast.success(hasPatient ? "Patient changed" : "Patient assigned");
        onSwapped();
      })
      .catch((err) => {
        toast.error(
          err?.response?.data?.error ||
            err?.response?.data?.message ||
            "Could not change patient"
        );
      })
      .finally(() => setSaving(false));
  };

  const handleSendSms = () => {
    if (!appointment?._id || smsSending) return;
    setSmsSending(true);
    baseUrl
      .post("/appointment/sendAppointmentConfirmSms", {}, { headers: { appointmentid: appointment._id } })
      .then((response) => {
        const sms = response.data?.sms;
        if (sms?.sent || response.data?.success) {
          toast.success(response.data?.message || "Confirmation SMS sent");
          notifySmsBalanceChanged();
          onClose();
        } else {
          toast.error(
            response.data?.message || response.data?.error || "Failed to send confirmation SMS"
          );
        }
      })
      .catch((err) => {
        toast.error(
          err?.response?.data?.error ||
            err?.response?.data?.message ||
            "Failed to send confirmation SMS"
        );
      })
      .finally(() => setSmsSending(false));
  };

  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center p-0 sm:p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="swap-patient-title"
    >
      <button
        type="button"
        className="absolute inset-0 bg-black/50 backdrop-blur-[2px]"
        onClick={() => !saving && onClose()}
        aria-label="Close dialog"
      />
      <div
        className="relative w-full sm:max-w-md max-h-[92vh] sm:max-h-[90vh] overflow-hidden rounded-t-2xl sm:rounded-2xl bg-white dark:bg-slate-800 shadow-2xl border border-slate-200 dark:border-slate-600 flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-4 flex items-center justify-between gap-3 border-b border-slate-200 dark:border-slate-600 shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center shrink-0">
              <SwapHorizIcon />
            </div>
            <div className="min-w-0">
              <h2
                id="swap-patient-title"
                className="text-base font-bold text-slate-900 dark:text-slate-100 truncate"
              >
                {hasPatient ? "Change patient" : "Assign patient"}
              </h2>
              <p className="text-xs text-slate-500 dark:text-slate-400 truncate">
                {hasPatient && currentName
                  ? `Currently booked for ${currentName}`
                  : "This appointment has no patient yet"}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="w-9 h-9 rounded-xl flex items-center justify-center text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors shrink-0 disabled:opacity-40"
            aria-label="Close"
          >
            <CloseIcon sx={{ fontSize: 20 }} />
          </button>
        </div>

        {result ? (
          <div className="p-5 space-y-4">
            <div className="rounded-xl border border-emerald-200 bg-emerald-50 dark:bg-emerald-900/20 dark:border-emerald-800 p-4">
              <p className="text-sm font-semibold text-emerald-800 dark:text-emerald-200">
                Appointment is now for {result.patientName || "the selected patient"}
              </p>
              {result.newPatientCreated && result.regNo ? (
                <p className="mt-1 text-xs text-emerald-700 dark:text-emerald-300">
                  New patient registered with reg no {result.regNo}. Complete their details
                  from the Patients page.
                </p>
              ) : null}
            </div>

            {result.hadToken ? (
              <div className="rounded-xl border border-amber-200 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-800 p-3 flex items-start gap-2">
                <PrintIcon className="text-amber-600 shrink-0" sx={{ fontSize: 18 }} />
                <p className="text-xs text-amber-800 dark:text-amber-200">
                  A token slip was already issued for this appointment. Reprint it so it shows
                  the new patient's name.
                </p>
              </div>
            ) : null}

            {result.mobile ? (
              <button
                type="button"
                onClick={handleSendSms}
                disabled={smsSending}
                className="w-full flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl text-sm font-semibold bg-sky-600 text-white hover:bg-sky-700 disabled:opacity-40 transition-colors"
              >
                <SmsOutlinedIcon sx={{ fontSize: 18 }} />
                {smsSending ? "Sending…" : `Send confirmation SMS to ${result.mobile}`}
              </button>
            ) : null}
            <button
              type="button"
              onClick={onClose}
              className="w-full px-4 py-2.5 rounded-xl text-sm font-semibold bg-slate-200/80 dark:bg-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-300 dark:hover:bg-slate-600 transition-colors"
            >
              Done
            </button>
          </div>
        ) : (
          <>
            <div className="overflow-y-auto flex-1 p-5 space-y-4">
              <AppointmentPatientStep
                formData={formData}
                setformData={setFormData}
                error={error}
                setError={setError}
                patientMode={patientMode}
                setPatientMode={setPatientMode}
              />
              {patientMode === "new" ? (
                <p className="text-xs text-slate-500">
                  A registration number is issued when you confirm. Other details can be
                  completed later from the Patients page.
                </p>
              ) : null}
            </div>

            <div className="shrink-0 px-5 py-4 border-t border-slate-200 dark:border-slate-600 bg-slate-50/90 dark:bg-slate-900/50 flex gap-2">
              <button
                type="button"
                onClick={onClose}
                disabled={saving}
                className="flex-1 px-4 py-2.5 rounded-xl text-sm font-semibold bg-slate-200/80 dark:bg-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-300 dark:hover:bg-slate-600 transition-colors disabled:opacity-40"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirm}
                disabled={saving}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold bg-primary text-white hover:opacity-90 disabled:opacity-40 transition-opacity"
              >
                <SwapHorizIcon sx={{ fontSize: 18 }} />
                {saving
                  ? "Saving…"
                  : patientMode === "new"
                    ? "Register & assign"
                    : hasPatient
                      ? "Change patient"
                      : "Assign patient"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>,
    document.body
  );
}
